import { DateTime } from "luxon";
import { useRef } from "react";

import { ProductsSettingsTable } from "#modules/company/view/ProductsSettingsTable";
import { makeCustomProductsCsv } from "#modules/products/domain/makeCustomProductsCsv";
import { useCreateProduct } from "#modules/products/view/useCreateProduct";
import { useDeleteProduct } from "#modules/products/view/useDeleteProduct";
import { useProducts } from "#modules/products/view/useProducts";
import { useUpdateProduct } from "#modules/products/view/useUpdateProduct";
import { useUploadCustomProducts } from "#modules/products/view/useUploadCustomProducts";
import { makeDownloadableUrl } from "#shared/helpers/file";
import { Icon } from "#shared/icons/Icon";
import { Anchor } from "#shared/view/components/Anchor";
import { Box } from "#shared/view/components/Box";
import { Group } from "#shared/view/components/Group";
import { Space } from "#shared/view/components/Space";
import { Stack } from "#shared/view/components/Stack";
import { Title } from "#shared/view/components/Title";
import { Button } from "#shared/view/designSystem/Button";
import { FileButton } from "#shared/view/designSystem/FileButton";
import { ProductTypeSchema } from "shared";

const DEFAULT_PRODUCT_NAME = "Nouveau produit";
const DEFAULT_PRODUCT_PRICE = 0;
const DEFAULT_PRODUCT_TYPE = ProductTypeSchema.enum.CONSUMABLE;

export const ProductsSettings = () => {
  const { products } = useProducts();
  const importProductsButtonRef = useRef<() => void>(null);

  const { uploadCustomProducts } = useUploadCustomProducts();
  const { updateProduct } = useUpdateProduct();
  const { deleteProduct } = useDeleteProduct();
  const { createProduct } = useCreateProduct();

  const uploadProductsCsv = (file: File | null) => {
    if (!file) {
      return;
    }
    uploadCustomProducts(file);
    importProductsButtonRef.current?.();
  };

  const makeCsvUrl = () => {
    const csvData = makeCustomProductsCsv(products);

    return makeDownloadableUrl(csvData, "text/csv");
  };

  const onUpdateCell = (rowId: string, columnId: string, value: unknown) => {
    updateProduct({
      id: rowId,
      payload: {
        [columnId]: value,
      },
    });
  };

  const onAddRow = async () => {
    await createProduct({
      name: DEFAULT_PRODUCT_NAME,
      price: DEFAULT_PRODUCT_PRICE,
      type: DEFAULT_PRODUCT_TYPE,
    });
  };

  const onRemoveRow = (rowId: string) => {
    deleteProduct(rowId);
  };

  const downloadButtonLabel =
    products.length === 0 ? "Télécharger le modèle" : "Télécharger le fichier";

  return (
    <Stack gap={0}>
      <Title order={5}>Consommables</Title>

      <Space h="sm" />
      <Group>
        <FileButton
          resetRef={importProductsButtonRef}
          onChange={uploadProductsCsv}
          accept="text/csv"
        >
          {(props) => (
            <Button variant="outline" icon={<Icon name="upload" />} {...props}>
              Importer un fichier
            </Button>
          )}
        </FileButton>
        <Anchor
          href={makeCsvUrl()}
          target="_blank"
          rel="noopener noreferrer"
          download={`export-fabera-${DateTime.now().toISODate()}.csv`}
        >
          <Button variant="outline" icon={<Icon name="download" />}>
            {downloadButtonLabel}
          </Button>
        </Anchor>
      </Group>
      <Space h="sm" />
      <Box miw={400} maw={800} w="70%">
        <ProductsSettingsTable
          data={products}
          onUpdateCell={onUpdateCell}
          onAddRow={onAddRow}
          onRemoveRow={onRemoveRow}
        />
      </Box>
      <Space h="lg" />
    </Stack>
  );
};
